/**
 * Costing desk. MASTER_PROMPT section 6.22 and decisions A16 and A17.
 *
 * Two things this screen refuses to let anyone forget:
 *
 *  - **Under- or over-absorption is explained, not plugged.** The unabsorbed
 *    reasons are shown as prominently as the totals.
 *  - **The confidence in the number is part of the number.** A cost per bag
 *    resting on apportionment is a different thing from one resting on meters,
 *    and the screen says which before it says how much.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../lib/api";
import { ops } from "../../lib/api2";
import type { CostRunResult, DriverRow } from "../../lib/types";
import { fmtINR, fmtPct, fmtQty, thisMonth, toNumber } from "../../lib/format";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

/** The two apportionment tables arrive as records keyed by name; a sheet
 *  needs rows, so they are flattened once here. */
type Absorbed = { name: string; amount: string };
type SkuCostRow = CostRunResult["sku_costs"][number];

const ABSORBED_COLUMNS: SheetColumn<Absorbed>[] = [
  { key: "name", label: "Cost centre", value: (r) => r.name },
  { key: "amount", label: "Absorbed", type: "number", numeric: true,
    value: (r) => toNumber(r.amount), format: (r) => fmtINR(r.amount) },
];

const ELEMENT_COLUMNS: SheetColumn<Absorbed>[] = [
  { key: "name", label: "Element", value: (r) => r.name },
  { key: "amount", label: "Amount", type: "number", numeric: true,
    value: (r) => toNumber(r.amount), format: (r) => fmtINR(r.amount) },
];

const SKU_COST_COLUMNS: SheetColumn<SkuCostRow>[] = [
  { key: "sku", label: "SKU", value: (r) => r.sku_code },
  { key: "bags", label: "Bags", type: "number", numeric: true,
    value: (r) => toNumber(r.bags), format: (r) => fmtQty(r.bags) },
  { key: "ink", label: "Ink per bag", type: "number", numeric: true,
    value: (r) => toNumber(r.ink_per_bag),
    format: (r) => fmtINR(r.ink_per_bag, { abbreviate: false }) },
  { key: "cost", label: "Cost per bag", type: "number", numeric: true,
    value: (r) => toNumber(r.cost_per_bag),
    format: (r) => fmtINR(r.cost_per_bag, { abbreviate: false }) },
];

const DRIVER_COLUMNS: SheetColumn<DriverRow>[] = [
  { key: "cost_centre", label: "Cost centre",
    value: (r) => r.cost_centre ?? "" },
  { key: "element", label: "Element",
    value: (r) => r.cost_element.replace(/_/g, " ").toLowerCase() },
  { key: "basis", label: "Basis",
    value: (r) => r.basis.replace(/_/g, " ").toLowerCase() },
  // MEASURED against APPORTIONED is the distinction the whole cost report
  // rests on, so it is the one column worth being able to filter on.
  { key: "source", label: "Source", value: (r) => r.source },
];

type Tab = "run" | "drivers";

export function CostingDesk() {
  const [tab, setTab] = useState<Tab>("run");
  // `thisMonth()`, not `toISOString()`: the latter converts to UTC first, so
  // in IST it names the previous month for the first five and a half hours of
  // every 1st.
  const [month, setMonth] = useState(thisMonth);
  const [result, setResult] = useState<CostRunResult | null>(null);
  const [drivers, setDrivers] = useState<DriverRow[] | null>(null);

  const byCostCentre = useMemo<Absorbed[]>(
    () => Object.entries(result?.by_cost_centre ?? {})
      .map(([name, amount]) => ({ name, amount: String(amount) })),
    [result]
  );
  const byElement = useMemo<Absorbed[]>(
    () => Object.entries(result?.by_element ?? {})
      .map(([name, amount]) => ({
        name: name.replace(/_/g, " ").toLowerCase(),
        amount: String(amount),
      })),
    [result]
  );

  const centres = useSheet(byCostCentre, ABSORBED_COLUMNS);
  const elements = useSheet(byElement, ELEMENT_COLUMNS);
  const skuCosts = useSheet(result?.sku_costs ?? [], SKU_COST_COLUMNS);
  const driverSheet = useSheet(drivers ?? [], DRIVER_COLUMNS);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  const loadDrivers = useCallback(() => {
    setDrivers(null);
    ops
      .drivers()
      .then(setDrivers)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, []);

  useEffect(() => {
    if (tab === "drivers") loadDrivers();
  }, [tab, loadDrivers]);

  async function run() {
    setBusy(true);
    setBanner(null);
    try {
      const r = await ops.runCosting(`${month}-01`);
      setResult(r);
      setBanner({
        tone: r.warnings.length ? "warning" : "positive",
        text: `Cost run complete for ${month}.`,
      });
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
      setResult(null);
    } finally {
      setBusy(false);
    }
  }

  async function seed() {
    setBusy(true);
    try {
      const r = await ops.seedDrivers(`${month}-01`);
      setBanner({ tone: "positive", text: `${r.created} drivers seeded. ${r.note}` });
      loadDrivers();
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  const measured = result ? Number(result.measured_pct) : 0;

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Absorption costing</h2>
          <p className="page__sub">
            A month-end batch, never a live calculation
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Period</span>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </label>
          <div className="segmented" role="tablist">
            {(
              [
                ["run", "Cost run"],
                ["drivers", "Drivers"],
              ] as [Tab, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                className={tab === id ? "is-active" : ""}
                onClick={() => setTab(id)}
                role="tab"
                aria-selected={tab === id}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {tab === "run" && (
        <>
          <div className="page__footer" style={{ marginBottom: "var(--sp-4)" }}>
            <span className="entrytotals__sub">
              Needs a classified trial balance for the month, imported from
              Tally. Absorption uses actual general-ledger expense, not an
              estimate.
            </span>
            <button className="btn btn--auto" onClick={run} disabled={busy}>
              {busy ? <span className="spinner" /> : null}
              Run costing
            </button>
          </div>

          {result && (
            <>
              {/* Confidence before quantum. A cost per bag built on estimates
                  is not the same number as one built on meters. */}
              <section
                className={`card confidence confidence--${
                  measured >= 70 ? "high" : measured >= 40 ? "mid" : "low"
                }`}
              >
                <div>
                  <span className="label">Rests on measurement</span>
                  <span className="kpi-value num">{fmtPct(measured)}</span>
                </div>
                <p className="confidence__note">
                  {measured >= 70
                    ? "Most of the absorbed cost is metered or directly issued. Cost per bag is a measurement."
                    : measured >= 40
                      ? "A material share is apportioned rather than measured. Treat cost per bag as indicative."
                      : "Almost everything is apportioned from plant-level totals. Cost per bag is an estimate — meters and payroll by cost centre would move this materially."}
                </p>
              </section>

              <div className="kpirow">
                <div className="kpi">
                  <span className="label">Cost per bag</span>
                  <span className="kpi-value num">
                    {result.cost_per_bag === null
                      ? "—"
                      : fmtINR(result.cost_per_bag, { abbreviate: false })}
                  </span>
                  <span className="kpi__sub">
                    {fmtQty(result.bags_produced)} bags
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">Cost per kg tape</span>
                  <span className="kpi-value num">
                    {result.cost_per_kg_tape === null
                      ? "—"
                      : fmtINR(result.cost_per_kg_tape, { abbreviate: false })}
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">Absorbable cost</span>
                  <span className="kpi-value num">
                    {fmtINR(result.total_cost)}
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">Absorbed</span>
                  <span className="kpi-value num">
                    {fmtINR(result.absorbed_cost)}
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">Under / over</span>
                  <span
                    className={`kpi-value num${
                      Number(result.under_over_absorption) !== 0
                        ? " kpi-value--warn"
                        : ""
                    }`}
                  >
                    {fmtINR(result.under_over_absorption)}
                  </span>
                  <span className="kpi__sub">
                    {fmtPct(result.under_over_pct)} of cost
                  </span>
                </div>
              </div>

              {result.unabsorbed_reasons.length > 0 && (
                <section className="card exceptions">
                  <h3 className="section-title">
                    Not absorbed — explained, never plugged
                  </h3>
                  <ul className="exceptions__list">
                    {result.unabsorbed_reasons.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="board2">
                <section className="card">
                  <h3 className="section-title">By cost centre</h3>
                  <div className="tablewrap">
                    <SheetToolbar sheet={centres} exportName="absorbed-by-cost-centre" />
                    <table className="table">
                      <SheetHead sheet={centres} />
                      <tbody>
                        {centres.rows
                          .map(({ name, amount }) => (
                            <tr key={name}>
                              <td>{name}</td>
                              <td className="num mono">{fmtINR(amount)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="card">
                  <h3 className="section-title">By element</h3>
                  <div className="tablewrap">
                    <SheetToolbar sheet={elements} exportName="cost-by-element" />
                    <table className="table">
                      <SheetHead sheet={elements} />
                      <tbody>
                        {elements.rows
                          .map(({ name: element, amount }) => (
                            <tr key={element}>
                              <td>{element}</td>
                              <td className="num mono">{fmtINR(amount)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </div>

              {result.sku_costs.length > 0 && (
                <section className="card">
                  <h3 className="section-title">Cost per bag by SKU</h3>
                  <div className="tablewrap">
                    <SheetToolbar sheet={skuCosts} exportName="cost-per-bag" />
                    <table className="table">
                      <SheetHead sheet={skuCosts} />
                      <tbody>
                        {skuCosts.rows.map((s) => (
                          <tr key={s.sku_code}>
                            <td>{s.sku_code}</td>
                            <td className="num mono">{fmtQty(s.bags)}</td>
                            <td className="num mono">
                              {fmtINR(s.ink_per_bag, { abbreviate: false })}
                            </td>
                            <td className="num mono">
                              {fmtINR(s.cost_per_bag, { abbreviate: false })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {result.warnings.map((w) => (
                <div key={w} className="banner banner--warning">
                  {w}
                </div>
              ))}
            </>
          )}
        </>
      )}

      {tab === "drivers" && (
        <>
          <section className="card">
            <h3 className="section-title">Absorption drivers</h3>
            <p className="section-note">
              How each cost element reaches each cost centre. Changing a row
              from apportioned to measured is configuration, not code — which is
              how meters and payroll get folded in as the floor audit delivers.
            </p>
            <div className="page__footer">
              <button className="btn btn--ghost btn--auto" onClick={seed} disabled={busy}>
                Seed defaults from {month}
              </button>
            </div>
          </section>

          {!drivers ? (
            <div className="state">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton state__bar" />
              ))}
            </div>
          ) : drivers.length === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ⚙
              </span>
              <h3>No drivers configured</h3>
              <p>Seed a starting set above, then tighten them one at a time.</p>
            </div>
          ) : (
            <div className="tablewrap tablewrap--tall">
              <SheetToolbar sheet={driverSheet} exportName="cost-drivers" />
              <table className="table">
                <SheetHead sheet={driverSheet} />
                <tbody>
                  {driverSheet.rows.map((d) => (
                    <tr key={d.id}>
                      <td>{d.cost_centre ?? "—"}</td>
                      <td>{d.cost_element.replace(/_/g, " ").toLowerCase()}</td>
                      <td className="mono">
                        {d.basis.replace(/_/g, " ").toLowerCase()}
                      </td>
                      <td>
                        <span
                          className={`pill${
                            d.source === "MEASURED" ? "" : " pill--warning"
                          }`}
                        >
                          {d.source === "MEASURED" ? "✓ measured" : "≈ apportioned"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
